import { useState, useEffect, useMemo } from "react"
import { ChevronsUpDown, Check } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList
} from "@/components/ui/command"
import { cn } from "@/lib/utils"

import { environment } from "@/env/env.prod"
import { apiHeaders } from "@/Auth/api-headers."

// ─────────────────────────────────────────────────────────────────────────
// Shared searchable subject picker — a Popover+Command combo backed by the
// Subject Registry (server-paginated, searchable by code/name), same
// pattern as the classroom picker already used in TakeAttendance.tsx /
// AttendanceRegistry.tsx. Replaces the old free-text subject code/name
// inputs and the "only what I've used before" filterOptions.subjects list.
//
// With programmeId (Take Attendance, for the chosen classroom's programme)
// it has two tabs: "This programme" — the programme's own subjects, searched
// here — and "All subjects" (the registry, as before). It opens on "This
// programme" when the programme has any.
// ─────────────────────────────────────────────────────────────────────────

export type SubjectOption = { id: string; subject_code: string; subject_name: string | null }

type Props = {
    // Currently selected subject code (for display + highlight)
    value: string
    onSelect: (subject: SubjectOption) => void
    placeholder?: string
    allowClear?: boolean
    onClear?: () => void
    clearLabel?: string   // the clear option's text (a filter says "All subjects")
    programmeId?: string | null
    programmeLabel?: string   // the tab's text, e.g. "This programme" / "This course"
}

export function SubjectCombobox({ value, onSelect, placeholder = "Select subject…", allowClear, onClear, clearLabel = "All subjects", programmeId = null, programmeLabel = "This programme" }: Props) {
    const [open, setOpen] = useState(false)
    const [query, setQuery] = useState("")
    const [results, setResults] = useState<SubjectOption[]>([])
    const [loading, setLoading] = useState(false)

    // the programme's own subjects, loaded once per programme
    const [programmeSubjects, setProgrammeSubjects] = useState<SubjectOption[]>([])
    const [tab, setTab] = useState<"programme" | "all">("all")
    useEffect(() => {
        setProgrammeSubjects([])
        setTab("all")
        if (!programmeId) return
        let alive = true
        fetch(`${environment.BASE_URL}/classrooms/listProgrammeSubjects?${new URLSearchParams({ programmeId })}`, { headers: apiHeaders() as HeadersInit })
            .then(r => r.json())
            .then(data => {
                if (!alive || !data.success) return
                const list: SubjectOption[] = (data.subjects || []).map((s: any) => ({ id: s.id, subject_code: s.subjectCode, subject_name: s.subjectName ?? null }))
                setProgrammeSubjects(list)
                if (list.length) setTab("programme")
            })
            .catch(() => { /* no programme tab then */ })
        return () => { alive = false }
    }, [programmeId])
    const showTabs = !!programmeId && programmeSubjects.length > 0
    const programmeShown = useMemo(() => {
        const q = query.trim().toLowerCase()
        return q ? programmeSubjects.filter(s => s.subject_code.toLowerCase().includes(q) || (s.subject_name || "").toLowerCase().includes(q)) : programmeSubjects
    }, [programmeSubjects, query])
    const listed = showTabs && tab === "programme" ? programmeShown : results

    // debounced, server-side search — same pattern as the classroom picker
    useEffect(() => {
        if (!open || (showTabs && tab === "programme")) return
        const t = setTimeout(async () => {
            setLoading(true)
            try {
                const params = new URLSearchParams({ search: query, limit: "20" })
                const res = await fetch(`${environment.BASE_URL}/subjects/listSubjects?${params}`, {
                    headers: apiHeaders() as HeadersInit,
                })
                const data = await res.json()
                setResults(data.success ? (data.subjects || []) : [])
            } catch {
                setResults([])
            } finally {
                setLoading(false)
            }
        }, 300)
        return () => clearTimeout(t)
    }, [query, open, tab, showTabs])

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className="w-full justify-between font-normal"
                >
                    <span className="truncate">{value || placeholder}</span>
                    <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0 ml-2" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
                <Command shouldFilter={false}>
                    {showTabs && (
                        <div className="flex gap-1 border-b p-1">
                            {([ [ "programme", `${programmeLabel} (${programmeSubjects.length})` ], [ "all", "All subjects" ] ] as const).map(([ v, label ]) => (
                                <button key={v} type="button" onClick={() => setTab(v)}
                                    className={cn("flex-1 rounded px-2 py-1 text-xs font-medium", tab === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted")}>
                                    {label}
                                </button>
                            ))}
                        </div>
                    )}
                    <CommandInput placeholder="Search by code or name…" value={query} onValueChange={setQuery} />
                    <CommandList>
                        <CommandEmpty>{loading && !(showTabs && tab === "programme") ? "Searching…" : "No subjects found."}</CommandEmpty>
                        <CommandGroup>
                            {allowClear && (
                                <CommandItem value="__all__" onSelect={() => { onClear?.(); setOpen(false) }}>
                                    {clearLabel}
                                </CommandItem>
                            )}
                            {listed.map(s => (
                                <CommandItem
                                    key={s.id}
                                    value={s.id}
                                    onSelect={() => { onSelect(s); setOpen(false) }}
                                >
                                    <Check className={cn("mr-2 h-4 w-4 shrink-0", value === s.subject_code ? "opacity-100" : "opacity-0")} />
                                    <div className="min-w-0">
                                        <div className="font-medium truncate">{s.subject_name || s.subject_code}</div>
                                        <div className="text-xs text-muted-foreground">Code: {s.subject_code}</div>
                                    </div>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    )
}
